import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import { z } from "zod";
import {
  SelectedUser,
  UpdateApiResponse,
  UserApiResponse,
  EntitiesApiResponse,
  ApiResponse,
  ManageEntityAccessPayload,
} from "./types/tenants";

const useTenants = () => {
  const fetchActiveUsers = async (
    skipCache: boolean = false,
    fields?: string, // Optional sparse fieldset parameter
  ) => {
    try {
      const response: UserApiResponse = await apiService(
        API_ROUTES_V2.FETCH_USERS,
        "GET",
        {
          suppressToast: true,
          skipCache,
          replace: {
            page: "1",
            pageSize: "100",
            role: "GXM_ADMIN,GXM_USER,GXM_MANAGER",
            searchValue: "",
            entity: "",
            service: "",
            includeUnassigned: "true", // GXM-1780: Include users with empty tenants array
            fields: fields || "", // Empty string if not provided
          },
          schema: z.object({
            page: z.string().optional(),
            pageSize: z.string().optional(),
            role: z.string().optional(),
            search: z.string().optional(),
            entity: z.string().optional(),
            service: z.string().optional(),
          }),
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to fetch users.");
    }
  };

  const updateUserTenants = async (selectedUser: SelectedUser) => {
    try {
      const response: UpdateApiResponse = await apiService(
        API_ROUTES_V2.UPDATE_USER,
        "PUT",
        {
          suppressToast: true,
          body: {
            assigned_tenants: selectedUser.tenants,
          },
          replace: { id: selectedUser?.id },
          schema: z.object({
            assigned_tenants: z.array(
              z.string().uuid("Tenant IDs are required"),
            ),
          }),
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to update user tenants.");
    }
  };

  const fetchTenantUsers = async (
    tenantId: string,
    page: number = 1,
    pageSize: number = 10,
    sortBy: string = "first_name",
    sortOrder: string = "asc",
    skipCache: boolean = false,
    fields?: string, // Optional sparse fieldset parameter
  ) => {
    try {
      const response = await apiService(
        API_ROUTES_V2.FETCH_USERS_BY_TENANT,
        "GET",
        {
          suppressToast: true,
          skipCache,
          replace: {
            tenant: tenantId,
            page: String(page),
            pageSize: String(pageSize),
            sortBy,
            sortOrder,
            fields: fields || "", // Empty string if not provided
          },
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to fetch user by tenants.");
    }
  };
  const fetchCustomerEntities = async () => {
    try {
      const response: EntitiesApiResponse = await apiService(
        API_ROUTES_V2.ALL_ENTITIES,
        "GET",
        {
          replace: {
            page: "1",
            pageSize: "100",
            searchValue: "",
          },
          schema: z.object({
            page: z.string().optional(),
            pageSize: z.string().optional(),
            search: z.string().optional(),
          }),
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to fetch entities");
    }
  };

  const addLspEntityAcceess = async (
    lspTenantId: string,
    payload: ManageEntityAccessPayload,
  ) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.ADD_TENANT_ENTITIES_ACCESS,
        "POST",
        {
          suppressToast: true,
          replace: { lspTenantId },
          body: payload,
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to add entity to Lsp tenant.");
    }
  };

  const updateLspEntityAcceess = async (
    lspTenantId: string,
    payload: ManageEntityAccessPayload,
  ) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.UPDATE_TENANT_ENTITY_ACCESS,
        "PUT",
        {
          suppressToast: true,
          replace: { lspTenantId, entityId: payload.entityId },
          body: payload,
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to add entity to Lsp tenant.");
    }
  };

  const fetchTenantDetails = async (id: string) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.FETCH_TENANT,
        "GET",
        {
          replace: { id },
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed tofetch tenant details.");
    }
  };

  return {
    fetchActiveUsers,
    updateUserTenants,
    fetchTenantUsers,
    fetchCustomerEntities,
    addLspEntityAcceess,
    updateLspEntityAcceess,
    fetchTenantDetails,
  };
};

export default useTenants;
